// Import standard module classes loaded through Yarn.
import Vue from 'vue';

// Modular loDash imports
import _debounce from 'lodash/debounce';

class Logger {
    /**
     * Logging constructor.
     *
     * @constructor
     */
    constructor () {
        // Define the default JavaScript logger type.
        this.type = 'info';

        // Create array to store log data objects in batches until debounce is fired.
        this.delayedLogsBatch = [];

        // Debounce to send errors in batch. This should be used instead of the toServer() method.
        this.send = _debounce(this.toServer, 1000);
    }

    /**
     * Make an XHR AJAX request to send the captured logs batch to the server critical error API endpoint to store within the server database.
     */
    toServer () {
        // Verify that any batched log events exist.
        if (! this.delayedLogsBatch.length) {
            return;
        }

        // Atempt to send the log data to the server to store within the database.
        try {
            const data = {};

            data[this.type + 's'] = this.delayedLogsBatch;

            Vue.axios.post('/log/javascript/error', data);
        } catch (error) {
            // If logging to the server fails for any reason our options are limited. Here we will log this error and the previous batch errors
            // to the console. This will be removed on minifcation so this will only output to the browser console in development environments.
            console.error(error, this.delayedLogsBatch); // eslint-disable-line no-console
        }

        // Once the debounced method is actually fired, clear the captured batch of logs.
        this.delayedLogsBatch = [];
    }
}

/**
 * The critical error logging class.
 */
class ErrorLogger extends Logger {
    /**
     * Critical error logging constructor.
     *
     * @constructor
     */
    constructor () {
        super();

        // Define the logger message type.
        this.type = 'error';

        // This will prevent continuous errors from being logged on a page in case of an infinite error loop or excessive false positives.
        this.maximumLogsPerPage = 4;
        this.logsCaptured = 0;
        this.isDisabled = false;
    }

    /**
     * This will process the logging request and send the provided log error data to the server.
     *
     * @param Object data The log error data.
     */
    log (data) {
        // If the logger has been disabled due to too many calls then exit the log script.
        if (this.isDisabled) {
            return;
        }

        // Increment the number of log requests that have been made.
        this.logsCaptured += 1;

        // Append log data to batch array.
        this.delayedLogsBatch.push(data);

        // Call the debounced send() method created within the constructor.
        this.send();

        // If the log requests made equals the maximum requests allowd then disabled the critical logger.
        if (this.logsCaptured === this.maximumLogsPerPage) {
            this.isDisabled = true;
        }
    }
}

// This will be a singleton class so we instantiate the export
export default new ErrorLogger();
